import type { UseQueryOptions } from '@tanstack/react-query'

import { type KeyContact } from 'src/types/keycontact'

import api from 'src/constants/api'

import useAccount from 'src/hooks/utils/useAccount'
import useQuery from '~/hooks/utils/useQuery'

const keyContactKeys = {
  all: ['keyContacts'] as const,
  byAccount: (accountId: string) => [...keyContactKeys.all, accountId] as const
}

const transformContact = (contact: KeyContact): KeyContact => {
  switch (contact.role) {
    case 'inside_sales_rep':
      contact.role = 'Account Manager'
      break
    case 'secondary_sales_rep':
      contact.role = 'Service Value Team Specialist'
      break
    case 'u_service_delivery_manager':
      contact.role = 'Service Delivery Manager'
      break
    case 'u_executive_sponsor':
      contact.role = 'Executive Sponsor'
      break
  }
  return contact
}

export const fetchKeyContacts = async (accountId: string, signal?: AbortSignal): Promise<KeyContact[]> => {
  if (!accountId) throw new Error('Account ID is missing')

  const response = await api.get(`/servicenow/account/${accountId}/details`, { signal })

  const roles = ['inside_sales_rep', 'secondary_sales_rep', 'u_service_delivery_manager', 'u_executive_sponsor']
  const contacts: KeyContact[] = []

  for (const role of roles) {
    if (response.data[0][`${role}.email`]) {
      contacts.push(
        transformContact({
          first_name: response.data[0][`${role}.first_name`],
          last_name: response.data[0][`${role}.last_name`],
          email: response.data[0][`${role}.email`],
          phone: response.data[0][`${role}.phone`],
          mobile: response.data[0][`${role}.mobile_phone`],
          role: role
        })
      )
    }
  }

  return contacts
}

const useKeyContactsQuery = (options?: UseQueryOptions<KeyContact[]>) => {
  const account = useAccount()

  return useQuery(keyContactKeys.byAccount(account.id), ({ signal }) => fetchKeyContacts(account.id, signal), {
    enabled: !!account.id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options
  })
}

export default useKeyContactsQuery
